import React, { Suspense, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';

import Content from 'components/styled/Content/Content';
import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import NotificationSettingsModal from '../../components/notifications/Settings';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import BannerNotifierApp from 'components/BannerNotifierApp';
import PlaylistActivityHeader from 'components/PlaylistActivity/PlaylistActivityHeader/PlaylistActivityHeader';
import { logToAmplitude } from '../../helpers/logAmplitudeEvent';
import SkeletonLoader from 'components/PlaylistActivityTracker/common/SkeletonLoader';
import { RootState } from 'redux/rootReducer';
import { connect } from 'react-redux';
import { IAuthenticationProfile } from 'redux/reducers/authentication';
import ErrorPage from 'pages/ErrorPage';
import checkBetaGroup from 'helpers/checkBetaGroup';

const Main = styled.div.attrs({
  role: 'main',
})`
  position: relative;
  z-index: 1;
  flex: auto;
  display: flex;
  flex-flow: column nowrap;
  background-color: ${({ theme }): string => theme.LightGrey};
`;

const PlaylistActivityTracker = React.lazy(
  () => import('components/PlaylistActivityTracker/index')
);

const PlaylistActivity: React.FC<{
  BetaGroupPriorityPlaylist?: string[];
  PriorityPlaylistOnlyForBeta?: boolean;
  userProfile: IAuthenticationProfile;
  isOpusNotifierOn?: boolean;
}> = ({
  BetaGroupPriorityPlaylist,
  PriorityPlaylistOnlyForBeta,
  userProfile,
  isOpusNotifierOn,
}) => {
  useEffect(() => {
    logToAmplitude('Playlist Activity Page Visited');
  }, []);

  const showPlaylistActivity =
    PriorityPlaylistOnlyForBeta === false ||
    checkBetaGroup(BetaGroupPriorityPlaylist, userProfile?.email);

  if (showPlaylistActivity)
    return (
      <>
        <ScrollTopOnMount />
        <Helmet defer={false} title="Playlist Activity" />

        <NotificationSettingsModal />

        <PlaylistActivityHeader />

        <Main>
          <Content>
            {/* Note: Do not remove the next <div>. It avoids flexbox to double margins between generic components. */}
            <div>
              <Suspense fallback={<SkeletonLoader title="Watchlist" isWide />}>
                <PlaylistActivityTracker />
              </Suspense>
            </div>
          </Content>
        </Main>
        <BackToTop />
        {isOpusNotifierOn && <BannerNotifierApp />}
        <Footer />
      </>
    );

  if (showPlaylistActivity === false) {
    return (
      <ErrorPage
        status={404}
        error={new Error('You do not have permission to visit this page yet.')}
      />
    );
  }

  return null;
};

const mapStateToProps = (
  state: RootState
): {
  BetaGroupPriorityPlaylist?: string[];
  PriorityPlaylistOnlyForBeta?: boolean;
  userProfile: IAuthenticationProfile;
  isOpusNotifierOn?: boolean;
} => ({
  BetaGroupPriorityPlaylist:
    state.consulEnvData?.data?.BetaGroupPriorityPlaylist,
  PriorityPlaylistOnlyForBeta:
    state.consulEnvData?.data?.PriorityPlaylistOnlyForBeta,
  userProfile: state.authentication.profile,
  isOpusNotifierOn: state.consulEnvData?.data?.isOpusNotifierOn,
});

export default connect(mapStateToProps)(PlaylistActivity);
